import React, { useCallback, useMemo, useState } from 'react';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { CustomVizOptionsSummaryDropdown } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { ObservationLocationType } from 'libs/end-to-end/src/observation-upload-type';
import { PayBenchmarkingRequest } from 'libs/end-to-end/src/custom-viz/PayBenchmarkingTypes';
import { PayBenchmarkingCompensationUnit } from 'libs/end-to-end/src/pay-benchmarking';
import { usePayBenchmarkingCanApplyFilters } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingCanApplyFilters';
import { usePayBenchmarkingCurrentState } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingCurrentState';
import { usePayBenchmarkingFilteredCustomVizDropdowns } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingFilteredCustomVizDropdowns';
import { usePayBenchmarkingGenerateShareLink } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingGenerateShareLink';
import { usePayBenchmarkingOnApplyFilters } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingOnApplyFilters';
import { usePayBenchmarkingOnDepartmentSelectedActions } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingOnDepartmentSelectedActions';
import { usePayBenchmarkingOnRoleSelectedActions } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingOnRoleSelectedActions';
import { usePayBenchmarkingUpdateSelectableDropdownsEffect } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingUpdateSelectableDropdownsEffect';
import { PayBenchmarkingContext } from 'apps/sigma2-ui/src/pay-benchmarking/components/PayBenchmarkingContext';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { usePayBenchmarkingCurrentCustomVizRequestToExecute } from 'apps/sigma2-ui/src/pay-benchmarking/hooks/usePayBenchmarkingCurrentCustomVizRequestToExecute';
import { useCopilotCustomVizAutoloader } from 'apps/sigma2-ui/src/lib/hooks/useCopilotCustomVizAutoloader';

export const PayBenchmarking = (props: {
  observation: ObservationLocationType;
  organizationId: string;
  children: React.ReactNode;
}) => {
  const { children, observation, organizationId } = props;

  const { userAuthenticatedApiCall } = useUser();

  const language = useLanguage();

  const context = useCustomViz();
  const { dropdownOptions, doViz, result, status } = useMemo(
    () => context,
    [context],
  );

  const [selectableBenchmarks, setSelectableBenchmarks] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableDepartments, setSelectableDepartments] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableRoles, setSelectableRoles] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableLevels, setSelectableLevels] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableLocations, setSelectableLocations] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableEducationLabels, setSelectableEducationLabels] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);
  const [selectableCurrencies, setSelectableCurrencies] = useState<
    CustomVizOptionsSummaryDropdown[]
  >([]);

  const selectableCompensationUnits: PayBenchmarkingCompensationUnit[] = [
    'salary',
    'hourly',
  ];

  // Selected Props
  const [selectedBenchmark, setSelectedBenchmark] = useState<string | null>(
    null,
  );
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(
    null,
  );
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedExperience, setSelectedExperience] = useState<string | null>(
    null,
  );
  const [selectedEducationLabel, setSelectedEducationLabel] = useState<
    string | null
  >(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [selectedCompensationUnit, setSelectedCompensationUnit] =
    useState<PayBenchmarkingCompensationUnit>('salary');

  const currentState = usePayBenchmarkingCurrentState({
    result,
    status,
  });

  const [shouldPrefillFilters, setShouldPrefillFilters] = useState(false);

  const canApplyFilters = usePayBenchmarkingCanApplyFilters({
    selectedBenchmark,
    selectedCurrency,
    selectedDepartment,
    selectedEducationLabel,
    selectedExperience,
    selectedLevel,
    selectedLocation,
    selectedRole,
  });

  const filteredCustomVizDropdowns =
    usePayBenchmarkingFilteredCustomVizDropdowns({
      dropdownOptions,
    });

  const onApplyFilters = usePayBenchmarkingOnApplyFilters({
    canApplyFilters,
    doViz,
    selectedBenchmark,
    selectedCompensationUnit,
    selectedCurrency,
    selectedDepartment,
    selectedEducationLabel,
    selectedExperience,
    selectedLevel,
    selectedLocation,
    selectedRole,
    observation,
    organizationId,
  });

  usePayBenchmarkingUpdateSelectableDropdownsEffect({
    setSelectableBenchmarks,
    setSelectableCurrencies,
    setSelectableEducationLabels,
    language,
    setSelectableLocations,
    filteredCustomVizDropdowns,
    dropdownOptions,
    setSelectableDepartments,
    shouldPrefillFilters,
  });

  const currentCustomVizRequestToExecute =
    usePayBenchmarkingCurrentCustomVizRequestToExecute({
      selectedBenchmark,
      selectedExperience,
      organizationId,
      selectedCompensationUnit,
      selectedDepartment,
      selectedCurrency,
      selectedLevel,
      selectedLocation,
      selectedEducationLabel,
      selectedRole,
      observation,
    });

  const generateShareLink = usePayBenchmarkingGenerateShareLink({
    currentState,
    userAuthenticatedApiCall,
    request: currentCustomVizRequestToExecute,
  });

  const onRoleSelectedActions = usePayBenchmarkingOnRoleSelectedActions({
    setSelectedRole,
    setSelectableLevels,
    setSelectedLevel,
    filteredCustomVizDropdowns,
  });

  const onDepartmentSelectedActions =
    usePayBenchmarkingOnDepartmentSelectedActions({
      setSelectableRoles,
      setSelectedRole,
      setSelectedDepartment,
      filteredCustomVizDropdowns,
    });

  const setSelections = useCallback((value: PayBenchmarkingRequest) => {
    setSelectedBenchmark(value.inputs.dropdowns.benchmark[0]);
    setSelectedCompensationUnit(value.inputs.dropdowns.compensation_unit[0]);
    setSelectedCurrency(value.inputs.dropdowns.currency[0]);
    setSelectedDepartment(value.inputs.dropdowns.department[0]);
    setSelectedEducationLabel(value.inputs.dropdowns.education[0]);
    setSelectedExperience(value.inputs.dropdowns.experience[0]);
    setSelectedLevel(value.inputs.dropdowns.job_level[0]);
    setSelectedLocation(value.inputs.dropdowns.location[0]);
    setSelectedRole(value.inputs.dropdowns.job_title[0]);
  }, []);

  const onAnalyseWithRequest = useCallback(
    (request: PayBenchmarkingRequest) => {
      doViz(request);
    },
    [doViz],
  );

  const resetFiltersToInitialState = useCallback(() => {
    setSelectedBenchmark(null);
    setSelectedDepartment(null);
    setSelectedRole(null);
    setSelectedLevel(null);
    setSelectedLocation(null);
    setSelectedExperience(null);
    setSelectedEducationLabel(null);
    setSelectedCurrency(null);
    setSelectedCompensationUnit('salary');
  }, []);

  const { onUserAnalysisCancel } = useCopilotCustomVizAutoloader({
    canAnalyze: canApplyFilters,
    onAnalyze: onApplyFilters,
    onAnalyseWithRequest,
    context,
    lastPersistedRequest: null,
    setSelections,
    setShouldPrefillFilters,
    shouldPrefillFilters,
    type: PayBenchmarkingRequest,
    resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  return (
    <PayBenchmarkingContext.Provider
      value={{
        selectableBenchmarks,
        selectedBenchmark,
        setSelectedBenchmark,
        selectableDepartments,
        selectedDepartment,
        setSelectedDepartment: onDepartmentSelectedActions,
        selectableRoles,
        selectedRole,
        setSelectedRole: onRoleSelectedActions,
        selectableLevels,
        selectedLevel,
        setSelectedLevel,
        selectableLocations,
        selectedLocation,
        setSelectedLocation,
        selectedExperience,
        setSelectedExperience,
        selectableEducationLabels,
        selectedEducationLabel,
        setSelectedEducationLabel,
        selectableCurrencies,
        selectedCurrency,
        setSelectedCurrency,
        selectableCompensationUnits,
        selectedCompensationUnit,
        setSelectedCompensationUnit,
        canApplyFilters,
        onApplyFilters,
        currentState,
        generateShareLink,
        onUserAnalysisCancel,
      }}
    >
      {children}
    </PayBenchmarkingContext.Provider>
  );
};
